import { FormEvent, Fragment, useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPatch, apiPost } from '@shared/api/httpClient';
import type { OutreachDeleteResponse, OutreachMutationResponse, OutreachRecord, OutreachResponse } from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel } from '@shared/utils/friendlyLabels';
import { statusPillClassName } from '@/utils/statusPill';

import OutreachActivity from '@/components/OutreachActivity';
import OutreachExport from '@/components/OutreachExport';
import { csvCell } from '@/utils/outreachActivity';

type OutreachStatusFilter = 'ALL' | 'ACTIVE' | 'REVOKED' | 'EXPIRED';
type OutreachAccessFilter = 'ALL' | 'YES' | 'NO';
type HumanAccessClassification = 'NO_ACCESS' | 'UNVERIFIED' | 'POSSIBLE_HUMAN' | 'ENGAGED' | 'HIGH_INTENT';

type OutreachSearchState = {
  status: OutreachStatusFilter;
  accessed: OutreachAccessFilter;
  company: string;
  recipient: string;
  email: string;
  campaign: string;
  search: string;
  createdFrom: string;
  createdTo: string;
  lastAccessFrom: string;
  lastAccessTo: string;
  limit: number;
};

type OutreachMetadata = Omit<OutreachResponse, 'items'>;
type OutreachEditState = Pick<OutreachRecord, 'company' | 'recipientName' | 'recipientEmail'> & {
  campaign: string;
  notes: string;
};

const DEFAULT_SEARCH: OutreachSearchState = {
  status: 'ALL',
  accessed: 'ALL',
  company: '',
  recipient: '',
  email: '',
  campaign: '',
  search: '',
  createdFrom: '',
  createdTo: '',
  lastAccessFrom: '',
  lastAccessTo: '',
  limit: 10,
};

function value(input: unknown): string {
  if (input === undefined || input === null || input === '') return '—';
  if (Array.isArray(input)) return input.join(', ') || '—';
  if (typeof input === 'object') return JSON.stringify(input);
  return String(input);
}

function rowKey(record: OutreachRecord): string {
  return record.invitationId;
}

function accessLabel(record: OutreachRecord): string {
  return record.accessed ? 'Access detected' : 'No access';
}

type HumanAccessAssessment = {
  classification: HumanAccessClassification;
  label: string;
  explanation: string;
  signal: string;
};

function videoProgress(record: OutreachRecord): number {
  if (record.videoCompletedCount > 0) return 100;
  if (record.video75PercentCount > 0) return 75;
  if (record.video50PercentCount > 0) return 50;
  if (record.video25PercentCount > 0) return 25;
  return 0;
}

function assessHumanAccess(record: OutreachRecord): HumanAccessAssessment {
  const progress = videoProgress(record);
  const deliberateAction = record.videoPlayCount > 0 || progress > 0 || record.scheduleDemoClickCount > 0;

  if (!record.accessed) {
    return {
      classification: 'NO_ACCESS',
      label: 'No access',
      explanation: 'The invitation URL has not been accessed.',
      signal: 'No activity',
    };
  }

  if ((record.likelyHumanHighIntentCount || 0) > 0) {
    const signal = record.scheduleDemoClickCount > 0
      ? `${record.scheduleDemoClickCount} schedule click${record.scheduleDemoClickCount === 1 ? '' : 's'}`
      : `${progress}% video viewed`;
    return {
      classification: 'HIGH_INTENT',
      label: 'High intent',
      explanation: 'A visible page reported trusted interaction with a strong engagement signal. This is heuristic evidence, not verified recipient identity.',
      signal,
    };
  }

  if ((record.likelyHumanEventCount || 0) > 0) {
    const signal = progress > 0
      ? `Video ${progress}% · ${record.videoPlayCount} play${record.videoPlayCount === 1 ? '' : 's'}`
      : record.videoPlayCount > 0 ? `${record.videoPlayCount} video plays` : 'Visible user interaction';
    return {
      classification: 'ENGAGED',
      label: 'Engaged',
      explanation: 'A visible page reported trusted interaction without a detected scanner marker. This suggests human activity but does not verify identity.',
      signal,
    };
  }

  if (deliberateAction) {
    return {
      classification: 'UNVERIFIED', label: 'Unverified interaction',
      explanation: 'Raw media or scheduling events exist, but their aggregates do not establish human activity. Expand Activity to inspect event attribution.',
      signal: progress > 0 ? `Video ${progress}% · attribution unverified` : 'Interaction · attribution unverified',
    };
  }

  const visibility = record.pageVisible30Count > 0
    ? '30s visibility only'
    : record.pageVisible10Count > 0
      ? '10s visibility only'
      : record.likelyAutomatedOpenCount > 0
        ? 'Automated client detected'
        : `${record.browserOpenCount} browser opens · no action`;
  return {
    classification: 'UNVERIFIED',
    label: 'Unverified',
    explanation: 'Access or page visibility was recorded without a deliberate action. This may be automated link processing.',
    signal: visibility,
  };
}

function humanSignalPillClassName(classification: HumanAccessClassification): string {
  if (classification === 'HIGH_INTENT') return 'status-pill status-pill-success outreach-signal-pill';
  if (classification === 'ENGAGED') return 'status-pill status-pill-info outreach-signal-pill';
  if (classification === 'POSSIBLE_HUMAN') return 'status-pill status-pill-warning outreach-signal-pill';
  return 'status-pill status-pill-muted outreach-signal-pill';
}

function activitySummary(record: OutreachRecord): string[] {
  if (!record.accessed) return ['No recorded activity'];

  const accessTypes = [
    record.browserOpenCount > 0 ? `${record.browserOpenCount} browser` : '',
    record.likelyAutomatedOpenCount > 0 ? `${record.likelyAutomatedOpenCount} automated` : '',
    record.unknownOpenCount > 0 ? `${record.unknownOpenCount} unknown` : '',
  ].filter(Boolean);
  const progress = videoProgress(record);
  const interactions = [
    record.pageVisible30Count > 0 ? `${record.pageVisible30Count} visible 30s` : '',
    progress > 0 ? `video ${progress}%` : record.videoPlayCount > 0 ? `${record.videoPlayCount} video play${record.videoPlayCount === 1 ? '' : 's'}` : '',
    record.scheduleDemoClickCount > 0 ? `${record.scheduleDemoClickCount} schedule click${record.scheduleDemoClickCount === 1 ? '' : 's'}` : '',
  ].filter(Boolean);

  return [accessTypes.join(' · '), interactions.join(' · ')].filter(Boolean);
}

function summarizePageActivity(records: OutreachRecord[]) {
  return records.reduce((summary, record) => {
    const classification = assessHumanAccess(record).classification;
    if (classification === 'HIGH_INTENT') summary.highIntent += 1;
    if (classification === 'ENGAGED') summary.engaged += 1;
    if (classification === 'POSSIBLE_HUMAN') summary.possibleHuman += 1;
    if (classification === 'UNVERIFIED') summary.unverified += 1;
    summary.automatedOpens += record.likelyAutomatedOpenCount;
    return summary;
  }, { highIntent: 0, engaged: 0, possibleHuman: 0, unverified: 0, automatedOpens: 0 });
}

function editState(record: OutreachRecord): OutreachEditState {
  return {
    company: record.company,
    recipientName: record.recipientName,
    recipientEmail: record.recipientEmail,
    campaign: record.campaign || '',
    notes: record.notes || '',
  };
}

const CSV_COLUMNS: Array<[string, keyof OutreachRecord]> = [
  ['Invitation ID', 'invitationId'], ['Company', 'company'], ['Recipient', 'recipientName'],
  ['Email', 'recipientEmail'], ['Bundle', 'bundleId'], ['Campaign', 'campaign'], ['Notes', 'notes'],
  ['Status', 'status'], ['Stored Status', 'storedStatus'], ['Created', 'createdAt'], ['Updated', 'updatedAt'],
  ['Expires', 'expiresAt'], ['Revoked', 'revokedAt'], ['Revocation Reason', 'revocationReason'],
  ['Accessed', 'accessed'], ['Open Count', 'openCount'], ['First Opened', 'firstOpenedAt'],
  ['Last Opened', 'lastOpenedAt'], ['First Client Type', 'firstOpenedClientType'],
  ['Last Client Type', 'lastOpenedClientType'], ['First User Agent', 'firstOpenedUserAgent'],
  ['Last User Agent', 'lastOpenedUserAgent'], ['First IP Hash', 'firstOpenedIpHash'],
  ['Last IP Hash', 'lastOpenedIpHash'], ['Last Request ID', 'lastOpenedRequestId'],
  ['Likely Automated Opens', 'likelyAutomatedOpenCount'], ['Browser Opens', 'browserOpenCount'],
  ['Unknown Opens', 'unknownOpenCount'], ['Raw Engaged Flag', 'engaged'], ['Raw Engagement Events', 'engagementEventCount'],
  ['Last Engagement', 'lastEngagementAt'], ['Last Engagement Type', 'lastEngagementType'],
  ['Visible 10 Seconds', 'pageVisible10Count'], ['Visible 30 Seconds', 'pageVisible30Count'],
  ['Video Plays', 'videoPlayCount'], ['Video 25%', 'video25PercentCount'], ['Video 50%', 'video50PercentCount'],
  ['Video 75%', 'video75PercentCount'], ['Video Completed', 'videoCompletedCount'],
  ['Schedule Clicks', 'scheduleDemoClickCount'],
  ['Likely Human Events', 'likelyHumanEventCount'], ['Likely Human High Intent Events', 'likelyHumanHighIntentCount'],
];

function downloadCsv(records: OutreachRecord[]): void {
  const headers = ['Human Access Classification', 'Human Signal', ...CSV_COLUMNS.map(([label]) => label)];
  const rows = [
    headers.map(csvCell).join(','),
    ...records.map((record) => {
      const assessment = assessHumanAccess(record);
      return [assessment.label, assessment.signal, ...CSV_COLUMNS.map(([, key]) => record[key])].map(csvCell).join(',');
    }),
  ];
  saveCsv(rows, `surelinc-outreach-${new Date().toISOString().slice(0, 10)}.csv`);
}

function saveCsv(rows: string[], filename: string): void {
  const blob = new Blob([`\uFEFF${rows.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function detailRow(label: string, fieldValue: unknown, className?: string) {
  return (
    <div className="detail-row">
      <dt>{label}</dt>
      <dd className={className}>{value(fieldValue)}</dd>
    </div>
  );
}

function OutreachDetails({
  record,
  busy,
  editRequest,
  onUpdated,
  onRevoke,
  onDelete,
}: {
  record: OutreachRecord;
  busy: boolean;
  editRequest: number;
  onUpdated: (record: OutreachRecord) => void;
  onRevoke: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<OutreachEditState>(() => editState(record));
  const [editError, setEditError] = useState<string | null>(null);
  const humanAssessment = assessHumanAccess(record);

  useEffect(() => {
    setForm(editState(record));
    setEditing(false);
    setEditError(null);
  }, [record]);

  useEffect(() => {
    if (editRequest > 0) setEditing(true);
  }, [editRequest]);

  async function submitEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEditError(null);
    setSaving(true);
    try {
      const response = await apiPatch<OutreachMutationResponse>(
        `/admin/outreach/${encodeURIComponent(record.invitationId)}`,
        { ...form, expectedUpdatedAt: record.updatedAt },
      );
      onUpdated(response.record);
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Unable to update the outreach record.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <article id={`outreach-details-${record.invitationId}`} className="card detail-panel">
      <div className="detail-panel-header">
        <div>
          <h3>Outreach record</h3>
          <p className="muted-text">Invitation, recipient, and secure demo access details.</p>
        </div>
        <span className={statusPillClassName(record.status)}>{friendlyLabel(record.status)}</span>
      </div>

      <div className="detail-grid">
        <section>
          <h4>Invitation</h4>
          <dl>
            {detailRow('Invitation ID', record.invitationId, 'mono-cell')}
            {detailRow('Company', record.company)}
            {detailRow('Bundle', record.bundleId)}
            {detailRow('Campaign', record.campaign)}
            {detailRow('Created', formatLocalDateTime(record.createdAt))}
            {detailRow('Expires', formatLocalDateTime(record.expiresAt))}
            {detailRow('Effective status', friendlyLabel(record.status))}
            {detailRow('Stored status', friendlyLabel(record.storedStatus))}
          </dl>
        </section>

        <section>
          <h4>Recipient and access</h4>
          <dl>
            {detailRow('Recipient', record.recipientName)}
            {detailRow('Email', record.recipientEmail)}
            {detailRow('Demo accessed', accessLabel(record))}
            {detailRow('Open count', record.openCount)}
            {detailRow('First opened', formatLocalDateTime(record.firstOpenedAt))}
            {detailRow('Last opened', formatLocalDateTime(record.lastOpenedAt))}
            {detailRow('First client type', friendlyLabel(record.firstOpenedClientType))}
            {detailRow('Last client type', friendlyLabel(record.lastOpenedClientType))}
            {detailRow('Likely automated opens', record.likelyAutomatedOpenCount)}
            {detailRow('Browser opens', record.browserOpenCount)}
            {detailRow('Unknown opens', record.unknownOpenCount)}
          </dl>
        </section>

        <section>
          <h4>Engagement</h4>
          <dl>
            {detailRow('Human access classification', humanAssessment.label)}
            {detailRow('Classification basis', humanAssessment.explanation)}
            {detailRow('Strongest signal', humanAssessment.signal)}
            {detailRow('Raw engagement flag', record.engaged ? 'Yes' : 'No')}
            {detailRow('Raw engagement events', record.engagementEventCount)}
            {detailRow('Last engagement', formatLocalDateTime(record.lastEngagementAt))}
            {detailRow('Last engagement type', friendlyLabel(record.lastEngagementType))}
            {detailRow('Visible 10 seconds', record.pageVisible10Count)}
            {detailRow('Visible 30 seconds', record.pageVisible30Count)}
            {detailRow('Video plays', record.videoPlayCount)}
            {detailRow('Video completed', record.videoCompletedCount)}
            {detailRow('Schedule clicks', record.scheduleDemoClickCount)}
          </dl>
        </section>

        <section className="detail-section-wide">
          <h4>Request identification</h4>
          <dl>
            {detailRow('First user agent', record.firstOpenedUserAgent, 'mono-cell')}
            {detailRow('Last user agent', record.lastOpenedUserAgent, 'mono-cell')}
            {detailRow('First IP hash', record.firstOpenedIpHash, 'mono-cell')}
            {detailRow('Last IP hash', record.lastOpenedIpHash, 'mono-cell')}
            {detailRow('Last request ID', record.lastOpenedRequestId, 'mono-cell')}
          </dl>
        </section>

        <section className="detail-section-wide">
          <h4>Administrative details</h4>
          <dl>
            {detailRow('Updated', formatLocalDateTime(record.updatedAt))}
            {detailRow('Revoked', formatLocalDateTime(record.revokedAt))}
            {detailRow('Revocation reason', record.revocationReason)}
            {detailRow('Notes', record.notes)}
          </dl>
        </section>
      </div>

      <div className="button-row outreach-record-actions">
        <button className="secondary-button" type="button" disabled={busy || saving} onClick={() => setEditing((current) => !current)}>
          {editing ? 'Cancel edit' : 'Edit metadata'}
        </button>
        {record.storedStatus !== 'REVOKED' && (
          <button className="danger-button" type="button" disabled={busy || saving} onClick={onRevoke}>Revoke demo link</button>
        )}
        <button className="danger-button" type="button" disabled={busy || saving} onClick={onDelete}>Delete record</button>
      </div>

      {editing && (
        <form className="form-grid outreach-edit-form" onSubmit={submitEdit}>
          <label>Company<input required maxLength={200} value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} /></label>
          <label>Contact name<input required maxLength={200} value={form.recipientName} onChange={(event) => setForm({ ...form, recipientName: event.target.value })} /></label>
          <label>Email address (optional)<input type="email" maxLength={320} value={form.recipientEmail} onChange={(event) => setForm({ ...form, recipientEmail: event.target.value })} /></label>
          <label>Campaign or contract name<input maxLength={200} value={form.campaign} onChange={(event) => setForm({ ...form, campaign: event.target.value })} /></label>
          <label className="form-wide">Notes<textarea maxLength={1000} rows={4} value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label>
          {editError && <p className="error-text form-wide">{editError}</p>}
          <div className="form-actions form-wide"><button className="primary-button" type="submit" disabled={busy || saving}>{saving ? 'Saving...' : 'Save metadata'}</button></div>
        </form>
      )}
    </article>
  );
}

export default function OutreachPage() {
  const [records, setRecords] = useState<OutreachRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<OutreachRecord | null>(null);
  const [search, setSearch] = useState<OutreachSearchState>(DEFAULT_SEARCH);
  const [appliedSearch, setAppliedSearch] = useState<OutreachSearchState>(DEFAULT_SEARCH);
  const [metadata, setMetadata] = useState<OutreachMetadata | null>(null);
  const [pageTokens, setPageTokens] = useState<Record<number, string | undefined>>({ 1: undefined });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState<{ invitationId: string; action: 'revoke' | 'delete' } | null>(null);
  const [editRequest, setEditRequest] = useState(0);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadOutreach(
    criteria: OutreachSearchState,
    pageNumber: number,
    cursor?: string,
  ) {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet<OutreachResponse>('/admin/outreach', {
        query: {
          limit: criteria.limit,
          page: pageNumber,
          cursor: cursor || undefined,
          status: criteria.status,
          accessed: criteria.accessed,
          company: criteria.company || undefined,
          recipient: criteria.recipient || undefined,
          email: criteria.email || undefined,
          campaign: criteria.campaign || undefined,
          search: criteria.search || undefined,
          createdFrom: criteria.createdFrom || undefined,
          createdTo: criteria.createdTo || undefined,
          lastAccessFrom: criteria.lastAccessFrom || undefined,
          lastAccessTo: criteria.lastAccessTo || undefined,
        },
      });

      const { items, ...responseMetadata } = response;
      setRecords(items);
      setSelectedRecord(null);
      setActivityId(null);
      setAppliedSearch(criteria);
      setMetadata(responseMetadata);
      setPage(response.page);
      setPageTokens((current) => {
        const next = { ...current, [pageNumber]: cursor };
        for (const key of Object.keys(next)) {
          if (Number(key) > pageNumber + 1) delete next[Number(key)];
        }
        if (response.nextToken) {
          next[pageNumber + 1] = response.nextToken;
        } else {
          delete next[pageNumber + 1];
        }
        return next;
      });
    } catch (err) {
      setRecords([]);
      setSelectedRecord(null);
      setMetadata(null);
      setError(err instanceof Error ? err.message : 'Unable to load outreach records.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOutreach(DEFAULT_SEARCH, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const criteria = { ...search, limit: Math.min(100, Math.max(1, search.limit)) };
    setSearch(criteria);
    setPage(1);
    setPageTokens({ 1: undefined });
    void loadOutreach(criteria, 1);
  }

  function handleClear() {
    setSearch(DEFAULT_SEARCH);
    setAppliedSearch(DEFAULT_SEARCH);
    setPage(1);
    setPageTokens({ 1: undefined });
    void loadOutreach(DEFAULT_SEARCH, 1);
  }

  function handlePrevious() {
    const previousPage = Math.max(1, page - 1);
    setSearch(appliedSearch);
    void loadOutreach(appliedSearch, previousPage, pageTokens[previousPage]);
  }

  function handleNext() {
    if (!metadata?.nextToken) return;
    const nextPage = page + 1;
    setSearch(appliedSearch);
    void loadOutreach(appliedSearch, nextPage, metadata.nextToken);
  }

  function replaceRecord(updated: OutreachRecord) {
    setRecords((current) => current.map((record) => record.invitationId === updated.invitationId ? updated : record));
    setSelectedRecord((current) => current?.invitationId === updated.invitationId ? updated : current);
  }

  function handleEdit(record: OutreachRecord) {
    setSelectedRecord(record);
    setEditRequest((current) => current + 1);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => document.getElementById(`outreach-details-${record.invitationId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
  }

  async function handleRevoke(record: OutreachRecord) {
    const reason = window.prompt('Optional revocation reason:', '') ?? null;
    if (reason === null) return;
    if (!window.confirm(`Revoke the demo link for ${record.company}? Existing sessions and future access will be denied.`)) return;
    setActionInFlight({ invitationId: record.invitationId, action: 'revoke' });
    setError(null);
    try {
      const response = await apiPost<OutreachMutationResponse>(
        `/admin/outreach/${encodeURIComponent(record.invitationId)}/revoke`,
        { reason, expectedUpdatedAt: record.updatedAt },
      );
      if (appliedSearch.status === 'ACTIVE') {
        await loadOutreach(appliedSearch, page, pageTokens[page]);
      } else {
        replaceRecord(response.record);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to revoke the demo link.');
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleDelete(record: OutreachRecord) {
    if (!window.confirm(`Permanently delete the outreach record for ${record.company}? This removes the invitation and aggregate tracking and immediately invalidates the demo link. Event audit records remain subject to their 365-day retention and are no longer accessible here. This cannot be undone.`)) return;
    setActionInFlight({ invitationId: record.invitationId, action: 'delete' });
    setError(null);
    try {
      await apiDelete<OutreachDeleteResponse>(
        `/admin/outreach/${encodeURIComponent(record.invitationId)}`,
        { query: { expectedUpdatedAt: record.updatedAt } },
      );
      await loadOutreach(appliedSearch, page, pageTokens[page]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete the outreach record.');
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleDownloadCsv() {
    setDownloadingCsv(true);
    setError(null);
    try {
      const exported: OutreachRecord[] = [];
      let cursor: string | undefined;
      let exportPage = 1;
      for (;;) {
        const response = await apiGet<OutreachResponse>('/admin/outreach', {
          query: {
            limit: 100,
            page: exportPage,
            cursor,
            status: appliedSearch.status,
            accessed: appliedSearch.accessed,
            company: appliedSearch.company || undefined,
            recipient: appliedSearch.recipient || undefined,
            email: appliedSearch.email || undefined,
            campaign: appliedSearch.campaign || undefined,
            search: appliedSearch.search || undefined,
            createdFrom: appliedSearch.createdFrom || undefined,
            createdTo: appliedSearch.createdTo || undefined,
            lastAccessFrom: appliedSearch.lastAccessFrom || undefined,
            lastAccessTo: appliedSearch.lastAccessTo || undefined,
          },
        });
        exported.push(...response.items);
        if (response.evaluationLimitReached) throw new Error('CSV export reached the query safety limit. Narrow the filters and try again.');
        if (!response.nextToken) break;
        if (exported.length >= 20_000) throw new Error('CSV export is limited to 20,000 records. Narrow the filters and try again.');
        cursor = response.nextToken;
        exportPage += 1;
      }
      downloadCsv(exported);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to download outreach records.');
    } finally {
      setDownloadingCsv(false);
    }
  }

  const pageActivity = summarizePageActivity(records);

  return (
    <div className="page-stack outreach-page">
      <div className="page-heading">
        <h2>Outreach</h2>
        <p>Search secure demo invitations and review prospect access activity. Results are ordered newest first.</p>
      </div>

      <article className="card">
        <h3>Search outreach</h3>
        <form className="form-grid jobs-search-grid" onSubmit={handleSearch}>
          <label className="form-wide">
            Search invitation, company, recipient, campaign, bundle, or notes
            <input value={search.search} onChange={(event) => setSearch({ ...search, search: event.target.value })} placeholder="Leidos, recipient email, campaign, invitation ID" />
          </label>
          <label>
            Company
            <input value={search.company} onChange={(event) => setSearch({ ...search, company: event.target.value })} placeholder="Company or organization" />
          </label>
          <label>
            Recipient name
            <input value={search.recipient} onChange={(event) => setSearch({ ...search, recipient: event.target.value })} placeholder="Recipient name" />
          </label>
          <label>
            Recipient email
            <input value={search.email} onChange={(event) => setSearch({ ...search, email: event.target.value })} placeholder="recipient@example.com" />
          </label>
          <label>
            Campaign
            <input value={search.campaign} onChange={(event) => setSearch({ ...search, campaign: event.target.value })} placeholder="Campaign or source" />
          </label>
          <label>
            Invitation status
            <select value={search.status} onChange={(event) => setSearch({ ...search, status: event.target.value as OutreachStatusFilter })}>
              <option value="ALL">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
              <option value="REVOKED">Revoked</option>
            </select>
          </label>
          <label>
            Demo accessed
            <select value={search.accessed} onChange={(event) => setSearch({ ...search, accessed: event.target.value as OutreachAccessFilter })}>
              <option value="ALL">All access states</option>
              <option value="YES">Access detected</option>
              <option value="NO">No access</option>
            </select>
          </label>
          <label>
            Created from
            <input type="date" value={search.createdFrom} onChange={(event) => setSearch({ ...search, createdFrom: event.target.value })} />
          </label>
          <label>
            Created through
            <input type="date" value={search.createdTo} onChange={(event) => setSearch({ ...search, createdTo: event.target.value })} />
          </label>
          <label>
            Last access from
            <input type="date" value={search.lastAccessFrom} onChange={(event) => setSearch({ ...search, lastAccessFrom: event.target.value })} />
          </label>
          <label>
            Last access through
            <input type="date" value={search.lastAccessTo} onChange={(event) => setSearch({ ...search, lastAccessTo: event.target.value })} />
          </label>
          <label>
            Results per page
            <select value={search.limit} onChange={(event) => setSearch({ ...search, limit: Number(event.target.value) })}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <div className="form-actions form-wide button-row">
            <button className="primary-button" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
            <button className="secondary-button" type="button" onClick={handleClear} disabled={loading}>Clear</button>
          </div>
        </form>
      </article>

      {metadata && !loading && !error && (
        <section className="dashboard-metric-grid outreach-metric-grid" aria-label="Current outreach page summary">
          <article className="metric-card metric-blue">
            <span>Page records</span>
            <strong>{metadata.summary.records}</strong>
            <p>Invitations on the current page</p>
          </article>
          <article className="metric-card metric-green">
            <span>Likely human interest</span>
            <strong>{pageActivity.highIntent + pageActivity.engaged}</strong>
            <p>{pageActivity.highIntent} high intent · {pageActivity.engaged} engaged</p>
          </article>
          <article className="metric-card metric-amber">
            <span>Repeat browser opens</span>
            <strong>{records.filter(record => record.browserOpenCount > 1).length}</strong>
            <p>Unverified; expand Activity to diagnose repeats</p>
          </article>
          <article className="metric-card metric-slate">
            <span>Unverified access</span>
            <strong>{pageActivity.unverified}</strong>
            <p>Access that may be automated link processing</p>
          </article>
          <article className="metric-card metric-amber">
            <span>Automated opens</span>
            <strong>{pageActivity.automatedOpens}</strong>
            <p>Opens attributed to automated clients</p>
          </article>
          <article className="metric-card metric-slate">
            <span>Total opens</span>
            <strong>{metadata.summary.totalOpens}</strong>
            <p>Recorded opens on the current page</p>
          </article>
        </section>
      )}

      <OutreachExport filters={appliedSearch} disabled={loading} />

      <article className="card">
        <div className="grid-header-row">
          <div>
            <h3>Outreach records</h3>
            <p className="muted-text">Human signals use reported trusted interactions and scanner checks. Browser opens, repeated initialization, and visibility alone remain unverified. Expand Activity for individual events.</p>
          </div>
          <div className="registry-header-actions">
            <button className="secondary-button" type="button" disabled={loading || downloadingCsv} onClick={() => void handleDownloadCsv()}>
              {downloadingCsv ? 'Preparing CSV...' : 'Download CSV'}
            </button>
            {metadata && (
              <span className="result-count">
                {records.length} record{records.length === 1 ? '' : 's'} · Page {metadata.page} · Newest first
              </span>
            )}
          </div>
        </div>

        {loading && <p>Loading outreach records...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && metadata?.evaluationLimitReached && (
          <p className="error-text">The query safety limit was reached before all matching records could be evaluated. Narrow the filters and search again.</p>
        )}
        {!loading && !error && records.length === 0 && <p>No outreach records matched the current filters.</p>}

        {metadata && !loading && !error && (metadata.hasPreviousPage || metadata.hasNextPage) && (
          <div className="pagination-row">
            <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={handlePrevious}>Previous</button>
            <span className="muted-text">Page {metadata.page}</span>
            <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={handleNext}>Next</button>
          </div>
        )}

        {!loading && !error && records.length > 0 && (
          <div className="table-wrap outreach-table-wrap">
            <table className="jobs-table interactive-table outreach-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Company</th>
                  <th>Recipient</th>
                  <th>Email</th>
                  <th>Access</th>
                  <th>Open count</th>
                  <th>Human signal</th>
                  <th>Evidence</th>
                  <th>Activity</th>
                  <th>Last access</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Campaign</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => {
                  const selected = selectedRecord === record;
                  const humanAssessment = assessHumanAccess(record);
                  const activity = activitySummary(record);
                  const revoking = actionInFlight?.invitationId === record.invitationId && actionInFlight.action === 'revoke';
                  const deleting = actionInFlight?.invitationId === record.invitationId && actionInFlight.action === 'delete';
                  return (
                    <Fragment key={rowKey(record)}>
                    <tr
                      className={selected ? 'selected-row' : undefined}
                      onClick={() => setSelectedRecord(record)}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') setSelectedRecord(record);
                      }}
                    >
                      <td>{formatLocalDateTime(record.createdAt)}</td>
                      <td>{value(record.company)}</td>
                      <td>{value(record.recipientName)}</td>
                      <td>{value(record.recipientEmail)}</td>
                      <td><span className={`status-pill ${record.accessed ? 'status-pill-info' : 'status-pill-muted'}`}>{accessLabel(record)}</span></td>
                      <td className="outreach-open-count">{record.openCount}</td>
                      <td>
                        <span
                          className={humanSignalPillClassName(humanAssessment.classification)}
                          title={humanAssessment.explanation}
                        >
                          {humanAssessment.label}
                        </span>
                      </td>
                      <td className="outreach-signal-evidence" title={humanAssessment.explanation}>{humanAssessment.signal}</td>
                      <td className="outreach-activity-cell">{activity.map((line) => <span key={line}>{line}</span>)}
                        <button className="secondary-button table-action-button" type="button" aria-expanded={activityId === record.invitationId} aria-controls={`activity-${record.invitationId}`}
                          onKeyDown={event => event.stopPropagation()}
                          onClick={event => { event.stopPropagation(); setActivityId(current => current === record.invitationId ? null : record.invitationId); }}>
                          {activityId === record.invitationId ? 'Hide activity' : 'Activity details'}
                        </button></td>
                      <td>{formatLocalDateTime(record.lastOpenedAt)}</td>
                      <td><span className={statusPillClassName(record.status)}>{friendlyLabel(record.status)}</span></td>
                      <td>{formatLocalDateTime(record.expiresAt)}</td>
                      <td>{value(record.campaign)}</td>
                      <td>
                        <div className="button-row outreach-row-actions">
                          <button className="secondary-button table-action-button" type="button" disabled={Boolean(actionInFlight)} onClick={(event) => { event.stopPropagation(); handleEdit(record); }}>Edit</button>
                          <button className="danger-button table-action-button" type="button" disabled={Boolean(actionInFlight) || record.storedStatus === 'REVOKED'} onClick={(event) => { event.stopPropagation(); void handleRevoke(record); }}>{revoking ? 'Revoking...' : 'Revoke'}</button>
                          <button className="danger-button table-action-button" type="button" disabled={Boolean(actionInFlight)} onClick={(event) => { event.stopPropagation(); void handleDelete(record); }}>{deleting ? 'Deleting...' : 'Delete'}</button>
                        </div>
                      </td>
                    </tr>
                    {activityId === record.invitationId && <tr id={`activity-${record.invitationId}`}><td colSpan={14}>
                      <OutreachActivity key={record.invitationId} invitationId={record.invitationId} />
                    </td></tr>}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {metadata && !loading && !error && (
          <p className="muted-text outreach-query-note">
            Evaluated {metadata.evaluatedCount} indexed record{metadata.evaluatedCount === 1 ? '' : 's'} for this page.
          </p>
        )}
      </article>

      {selectedRecord && (
        <OutreachDetails
          record={selectedRecord}
          busy={Boolean(actionInFlight)}
          editRequest={editRequest}
          onUpdated={replaceRecord}
          onRevoke={() => void handleRevoke(selectedRecord)}
          onDelete={() => void handleDelete(selectedRecord)}
        />
      )}
    </div>
  );
}
